import { useState, useEffect } from 'react'
import { Package } from 'lucide-react'
import { motion } from 'framer-motion'
import '@/screens/Modals/OpenPackModal.css'
import { callOpen } from '@/helpers/api'
import { useCachedPublicImage } from '@/helpers/urls'
import { UserCard } from 'common/schema/users/userCards'

type OpenPackModalProps = {
  isOpen: boolean
  onClose: () => void
  packId: string
  onSuccess: (cards: UserCard[]) => void
}

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const cardVariants = {
  hidden: { rotateY: -180, opacity: 0 },
  visible: { rotateY: 0, opacity: 1 },
}

// Card component that uses cached image
const RevealedCard: React.FC<{ card: UserCard }> = ({ card }) => {
  const cachedImageUrl = useCachedPublicImage(card.url || '')

  return (
    <div className="pack-card-container">
      <img src={cachedImageUrl} alt={card.name || 'Card'} />
    </div>
  )
}

export const OpenPackModal = ({
  isOpen,
  onClose,
  packId,
  onSuccess,
}: OpenPackModalProps) => {
  const [isOpening, setIsOpening] = useState(false)
  const [cards, setCards] = useState<UserCard[]>([])
  const [stage, setStage] = useState<'initial' | 'opening' | 'revealed'>(
    'initial'
  )
  const [loadingText, setLoadingText] = useState('Click to Open Pack')

  useEffect(() => {
    if (!isOpen) {
      setStage('initial')
      setCards([])
      setIsOpening(false)
      setLoadingText('Click to Open Pack')
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpening) {
      const texts = [
        'Opening Pack...',
        'Channeling Magic...',
        'Almost There...',
      ]
      let currentIndex = 0

      const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % texts.length
        setLoadingText(texts[currentIndex])
      }, 2000)

      return () => clearInterval(interval)
    }
  }, [isOpening])

  const handleOpenPack = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isOpening || stage !== 'initial') return

    setStage('opening')
    setIsOpening(true)
    setLoadingText('Opening Pack...')

    try {
      const result = await callOpen({ userCardPackId: packId })

      if (result.success) {
        const data = result.response.data as {
          success: boolean
          cards: UserCard[]
        }

        setCards(data.cards)

        setTimeout(() => {
          setStage('revealed')
          onSuccess(data.cards)
        }, 1500)
      }
    } catch (error) {
      console.error('Error opening pack:', error)
      setStage('initial')
      setLoadingText('Click to Open Pack')
    } finally {
      setIsOpening(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="pack-modal-overlay" onClick={onClose}>
      <div className="pack-modal-content">
        {stage !== 'revealed' ? (
          <div className="pack-container" onClick={handleOpenPack}>
            <div className={`pack ${stage === 'opening' ? 'opening' : ''}`}>
              <div className="pack-face front">
                <Package size={64} color="white" />
              </div>
              <div className="pack-face back" />
            </div>
            <div className="loading-text">{loadingText}</div>
          </div>
        ) : (
          <motion.div
            className="pack-revealed-content"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            {cards.map((card, index) => (
              <motion.div
                key={card.userCardId}
                className="pack-card-wrapper"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.3 }}
              >
                <RevealedCard card={card} />
                <div className="pack-card-text">
                  <h2>{card.name || card.collection}</h2>
                  <p>
                    <span
                      className={`reveal-rarity-badge reveal-rarity-${card.rarity?.toLowerCase()}`}
                    >
                      {card.rarity}
                    </span>
                  </p>
                </div>
              </motion.div>
            ))}
            <span className="pack-instruction">Tap anywhere to close</span>
          </motion.div>
        )}
      </div>
    </div>
  )
}
